/**
 * PixelReader — turns encoded image bytes into greyscale pixels.
 * Wraps createImageBitmap and OffscreenCanvas, and nothing else.
 *
 * Two ways in, because sharpness is measured twice. `grayscale` decodes the
 * 240px thumbnail m3cull has already produced — a millisecond or so per frame —
 * for the screening pass that runs on every shoot. `grayscaleAt` goes back to
 * the original for the review the photographer opted into, at a size where a
 * missed focus is still visible.
 */

import { toGrayscale, type Grayscale } from '../domain/sharpness';
import { fitWithin } from '../domain/preview-size';
import { forDom } from './bytes';

export interface PixelReader {
  /** Decode encoded bytes and return one luma byte per pixel. */
  grayscale(bytes: Uint8Array): Promise<Grayscale>;

  /**
   * Decode a photograph straight to luma at a bounded size.
   *
   * For measuring, not for showing, and the difference matters: nothing is
   * encoded, so no JPEG artefact is added to the very high-frequency detail the
   * measure is reading, and nothing is stored, so a shoot of three thousand
   * frames does not leave three thousand renders behind it.
   */
  grayscaleAt(source: Blob, longEdge: number): Promise<Grayscale>;
}

export class CanvasPixelReader implements PixelReader {
  static get isSupported(): boolean {
    return typeof createImageBitmap === 'function' && typeof OffscreenCanvas === 'function';
  }

  async grayscale(bytes: Uint8Array): Promise<Grayscale> {
    return this.read(await createImageBitmap(new Blob([forDom(bytes)])));
  }

  async grayscaleAt(source: Blob, longEdge: number): Promise<Grayscale> {
    // Two decodes would be the obvious way to do this and the wrong one. Asking
    // createImageBitmap for the size up front lets the browser downsample during
    // the decode rather than after it, which is the difference between holding a
    // 24 MP bitmap in memory and holding a 1 MP one.
    const probe = await createImageBitmap(source, { imageOrientation: 'from-image' });
    const { width, height } = fitWithin(probe.width, probe.height, longEdge);
    if (width === probe.width && height === probe.height) return this.read(probe);

    probe.close();
    return this.read(
      await createImageBitmap(source, {
        imageOrientation: 'from-image',
        resizeWidth: width,
        resizeHeight: height,
        resizeQuality: 'high',
      }),
    );
  }

  private read(bitmap: ImageBitmap): Grayscale {
    try {
      const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
      const ctx = canvas.getContext('2d', { alpha: false, willReadFrequently: true });
      if (!ctx) throw new Error('No 2D context for reading pixels.');

      ctx.drawImage(bitmap, 0, 0);
      const { data } = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
      return toGrayscale(data, bitmap.width, bitmap.height);
    } finally {
      bitmap.close();
    }
  }
}
